/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Car, Loader2, RefreshCw, Hand, Zap, ArrowUp, ArrowDown, Trash2, Plus,
  CheckCircle2, CircleDot, Save, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { AreaPinGate } from '@/components/AreaPinGate';

const SYM = { eur: '€', usd: '$', gbp: '£', pln: 'zł', sek: 'kr' };
const money = (a, sym = '€') => `${Number(a || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${sym}`;

function capPct(ca, cap) {
  if (!cap || cap <= 0) return 0;
  return Math.min(100, Math.round((ca / cap) * 100));
}
function barColor(pct, hasCap) {
  if (!hasCap) return 'bg-zinc-300';
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 70) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function PoolRow({ row, idx, total, onCap, onUp, onDown, onRemove, onActivate, activating }) {
  const hasCap = Number(row.daily_cap_eur) > 0;
  const pct = capPct(row.ca_today, row.daily_cap_eur);
  const sym = SYM[row.currency] || (row.currency || 'eur').toUpperCase();
  return (
    <div
      className={`rounded-xl border p-4 transition ${row.is_active ? 'border-emerald-300 bg-emerald-50/60 ring-1 ring-emerald-200' : 'border-zinc-200 bg-white'}`}
      data-testid={`batmobile-pool-row-${row.vitrine_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-1 pt-1">
          <button type="button" onClick={onUp} disabled={idx === 0}
            className="h-6 w-6 rounded grid place-items-center text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
            data-testid={`batmobile-up-${row.vitrine_id}`} title="Monter dans l'ordre"><ArrowUp className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={onDown} disabled={idx === total - 1}
            className="h-6 w-6 rounded grid place-items-center text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
            data-testid={`batmobile-down-${row.vitrine_id}`} title="Descendre dans l'ordre"><ArrowDown className="h-3.5 w-3.5" /></button>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-zinc-900 text-white text-xs font-bold">{idx + 1}</span>
            <span className="font-semibold truncate">{row.vitrine_name}</span>
            {row.is_active && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="h-3 w-3" /> Active — encaisse maintenant
              </span>
            )}
          </div>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">Plafond / jour</span>
              <div className="relative">
                <Input
                  type="number" min="0" step="50"
                  value={row.daily_cap_eur || ''}
                  onChange={(e) => onCap(Number(e.target.value || 0))}
                  placeholder="0 = illimité"
                  className="h-9 w-32 rounded-md pr-6"
                  data-testid={`batmobile-cap-${row.vitrine_id}`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">{sym}</span>
              </div>
              <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-mono">{(row.currency || 'eur').toUpperCase()}</span>
            </div>
            <div className="text-sm">
              <span className="text-zinc-500">CA aujourd'hui : </span>
              <span className="font-semibold tabular-nums">{money(row.ca_today, sym)}</span>
              {hasCap && <span className="text-zinc-400"> / {money(row.daily_cap_eur, sym)}</span>}
            </div>
          </div>

          <div className="mt-2 h-2 w-full rounded-full bg-zinc-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${barColor(pct, hasCap)}`} style={{ width: `${hasCap ? pct : 0}%` }} />
          </div>
          {hasCap && pct >= 100 && (
            <p className="mt-1 text-[11px] text-red-600 font-medium">Plafond atteint — la rotation auto basculera sur la vitrine suivante.</p>
          )}
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          {!row.is_active && (
            <Button size="sm" variant="outline" className="rounded-md text-xs"
              onClick={onActivate} disabled={activating}
              data-testid={`batmobile-activate-${row.vitrine_id}`}>
              {activating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CircleDot className="h-3.5 w-3.5 mr-1" /> Activer</>}
            </Button>
          )}
          <button type="button" onClick={onRemove}
            className="h-8 w-8 rounded-md grid place-items-center text-zinc-400 hover:text-red-600 hover:bg-red-50"
            data-testid={`batmobile-remove-${row.vitrine_id}`} title="Retirer du pool"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

function RotationPanel({ stores, vitrines }) {
  const [storeId, setStoreId] = useState(stores[0]?.id || '');
  const [mode, setMode] = useState('manual');
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState('');
  const [addVid, setAddVid] = useState('');
  const [dirty, setDirty] = useState(false);
  // Read latest `dirty` inside the polling interval without resetting the timer.
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  const load = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const r = await api.get(`/stores/${sid}/rotation`);
      setMode(r.data.mode || 'manual');
      setPool(r.data.pool || []);
      setDirty(false); // fresh server state = no unsaved edits
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  // Non-destructive live CA refresh: only updates ca_today / is_active on rows
  // that already exist locally (matched by vitrine_id). NEVER touches caps,
  // order, mode or the set of rows — so it can't wipe in-progress edits.
  const refreshCa = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const r = await api.get(`/stores/${sid}/rotation`);
      const fresh = {};
      (r.data.pool || []).forEach((e) => { fresh[e.vitrine_id] = e; });
      setPool((cur) => cur.map((row) => (
        fresh[row.vitrine_id]
          ? { ...row, ca_today: fresh[row.vitrine_id].ca_today, is_active: fresh[row.vitrine_id].is_active }
          : row
      )));
    } catch { /* silent — background refresh */ }
  }, []);

  useEffect(() => { if (storeId) load(storeId); }, [storeId, load]);
  // Live CA refresh every 25s — non-destructive AND paused while there are
  // unsaved edits, so it never makes you lose work in progress.
  useEffect(() => {
    if (!storeId) return undefined;
    const t = setInterval(() => { if (!dirtyRef.current) refreshCa(storeId); }, 25000);
    return () => clearInterval(t);
  }, [storeId, refreshCa]);

  const vitrineName = (id) => vitrines.find((v) => v.id === id)?.name || '—';
  const vitrineCurrency = (id) => vitrines.find((v) => v.id === id)?.currency || 'eur';
  const available = vitrines.filter((v) => !pool.some((p) => p.vitrine_id === v.id));

  const addVitrine = () => {
    if (!addVid) return;
    setPool((p) => [...p, { vitrine_id: addVid, vitrine_name: vitrineName(addVid), currency: vitrineCurrency(addVid), daily_cap_eur: 0, ca_today: 0, is_active: false }]);
    setAddVid('');
    setDirty(true);
  };
  const move = (i, dir) => { setDirty(true); setPool((p) => {
    const a = [...p]; const j = i + dir;
    if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j], a[i]];
    return a;
  }); };
  const setCap = (i, val) => { setDirty(true); setPool((p) => p.map((r, k) => (k === i ? { ...r, daily_cap_eur: val } : r))); };
  const removeRow = (i) => { setDirty(true); setPool((p) => p.filter((_, k) => k !== i)); };
  const changeMode = (m) => { setMode(m); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/stores/${storeId}/rotation`, {
        mode,
        pool: pool.map((p) => ({ vitrine_id: p.vitrine_id, daily_cap_eur: Number(p.daily_cap_eur || 0) })),
      });
      setMode(r.data.mode);
      setPool(r.data.pool || []);
      setDirty(false);
      toast.success('Rotation enregistrée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const activate = async (vid) => {
    setActivating(vid);
    try {
      const r = await api.post(`/stores/${storeId}/rotation/activate`, { vitrine_id: vid });
      setMode(r.data.mode);
      setPool(r.data.pool || []);
      setDirty(false);
      toast.success(`Vitrine activée — c'est ce Stripe qui encaisse désormais`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Activation impossible');
    } finally {
      setActivating('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <label className="text-xs text-zinc-500 mb-1 block">Boutique</label>
          <Select value={storeId} onValueChange={setStoreId}>
            <SelectTrigger className="h-10 rounded-md" data-testid="batmobile-store-select"><SelectValue placeholder="Choisir une boutique" /></SelectTrigger>
            <SelectContent>
              {stores.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`batmobile-store-${s.id}`}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs text-zinc-500 mb-1 block">Mode</label>
          <div className="grid grid-cols-2 gap-1 p-1 bg-zinc-100 rounded-lg w-[260px]">
            {[
              { key: 'manual', label: 'Manuel', icon: Hand },
              { key: 'auto', label: 'Auto (par CA)', icon: Zap },
            ].map((m) => (
              <button key={m.key} type="button" onClick={() => changeMode(m.key)}
                data-testid={`batmobile-mode-${m.key}`}
                className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-semibold transition ${mode === m.key ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-800'}`}>
                <m.icon className="h-4 w-4" />{m.label}
              </button>
            ))}
          </div>
        </div>

        <Button variant="outline" className="rounded-md h-10" onClick={() => load(storeId)} disabled={loading} data-testid="batmobile-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button className="rounded-md h-10 bg-zinc-950" onClick={save} disabled={saving || !storeId} data-testid="batmobile-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Enregistrer
        </Button>
        {dirty && (
          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-md" data-testid="batmobile-dirty-badge">
            <AlertCircle className="h-3.5 w-3.5" /> Modifications non enregistrées
          </span>
        )}
      </div>

      <p className="text-sm text-zinc-500">
        {mode === 'auto'
          ? "Mode auto : la 1ʳᵉ vitrine sous son plafond encaisse. Dès qu'elle atteint son plafond du jour (minuit Paris), on passe automatiquement à la suivante. Si toutes sont pleines, on reste sur la dernière."
          : "Mode manuel : choisis la vitrine qui encaisse avec « Activer ». La rotation auto est désactivée."}
        {' '}Chaque plafond et son CA sont exprimés dans la <strong>devise propre</strong> de la vitrine (celle de son compte Stripe) — pas de conversion entre devises.
      </p>

      {/* Pool */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>
      ) : pool.length === 0 ? (
        <Card className="card-flat p-10 text-center">
          <Car className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
          <h3 className="font-display font-bold mb-1">Aucune vitrine dans le pool</h3>
          <p className="text-zinc-500 text-sm">Ajoute les vitrines (= comptes Stripe) entre lesquelles cette boutique doit tourner.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="batmobile-pool">
          {pool.map((row, i) => (
            <PoolRow key={row.vitrine_id} row={row} idx={i} total={pool.length}
              onCap={(v) => setCap(i, v)} onUp={() => move(i, -1)} onDown={() => move(i, 1)}
              onRemove={() => removeRow(i)} onActivate={() => activate(row.vitrine_id)}
              activating={activating === row.vitrine_id} />
          ))}
        </div>
      )}

      {/* Add vitrine */}
      {available.length > 0 && (
        <div className="flex items-center gap-2">
          <Select value={addVid} onValueChange={setAddVid}>
            <SelectTrigger className="h-10 rounded-md w-72" data-testid="batmobile-add-select"><SelectValue placeholder="Ajouter une vitrine au pool…" /></SelectTrigger>
            <SelectContent>
              {available.map((v) => <SelectItem key={v.id} value={v.id} data-testid={`batmobile-add-${v.id}`}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-md h-10" onClick={addVitrine} disabled={!addVid} data-testid="batmobile-add-btn">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
          <span className="text-xs text-zinc-400">N'oublie pas d'Enregistrer.</span>
        </div>
      )}
    </div>
  );
}

export default function Batmobile() {
  const [stepToken, setStepToken] = useState('');
  const [stores, setStores] = useState(null);
  const [vitrines, setVitrines] = useState([]);

  useEffect(() => {
    if (!stepToken) return;
    (async () => {
      try {
        const [s, v] = await Promise.all([api.get('/stores'), api.get('/vitrines')]);
        const list = Array.isArray(s.data) ? s.data : (s.data.items || []);
        setStores(list);
        setVitrines(Array.isArray(v.data) ? v.data : []);
      } catch {
        toast.error('Chargement impossible');
        setStores([]);
      }
    })();
  }, [stepToken]);

  if (!stepToken) {
    return (
      <AreaPinGate
        area="batmobile"
        title="La Batmobile"
        subtitle="Code d'accès pour piloter la rotation des Stripe"
        icon={Car}
        onUnlock={setStepToken}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl">
      <div className="mb-8">
        <div className="overline mb-2 flex items-center gap-1.5"><Car className="h-3.5 w-3.5" /> Rotation Stripe</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">La Batmobile</h1>
        <p className="text-zinc-600 mt-2">Choisis sur quel Stripe (= quelle vitrine) chaque boutique encaisse — manuellement, ou automatiquement par plafond de CA journalier.</p>
      </div>

      {stores === null ? (
        <div className="flex items-center justify-center py-24 text-zinc-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement…</div>
      ) : stores.length === 0 ? (
        <Card className="card-flat p-10 text-center">
          <h3 className="font-display font-bold mb-1">Aucune boutique</h3>
          <p className="text-zinc-500 text-sm">Crée d'abord une boutique et des vitrines à faire tourner.</p>
        </Card>
      ) : (
        <RotationPanel stores={stores} vitrines={vitrines} />
      )}
    </div>
  );
}
